import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { Pill, CalendarDays, AlertTriangle, ClipboardList } from "lucide-react";
import { StatCard, Card, CardHeader, CardTitle, CardContent, buttonVariants, cn, PageLoading, EmptyState } from "@dosa/ui";
import { useAuth } from "../../hooks/useAuth";
import { listPatientMedications, listPatientAppointments, listMtmAssessments } from "../../lib/api-client";

export function PatientDashboardPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const patientId = user!.patientId!;

  const medsQuery = useQuery({ queryKey: ["patient-medications", patientId], queryFn: () => listPatientMedications(patientId) });
  const apptsQuery = useQuery({ queryKey: ["patient-appointments", patientId], queryFn: () => listPatientAppointments(patientId) });
  const assessmentsQuery = useQuery({ queryKey: ["patient-mtm", patientId], queryFn: () => listMtmAssessments(patientId) });

  if (medsQuery.isLoading || apptsQuery.isLoading) return <PageLoading />;

  const activeMeds = medsQuery.data?.filter((m) => m.status === "ACTIVE") ?? [];
  const upcoming = apptsQuery.data?.filter((a) => a.status === "CONFIRMED" || a.status === "REQUESTED") ?? [];
  const openAssessments = assessmentsQuery.data?.filter((a) => a.status !== "COMPLETED" && a.status !== "CANCELLED") ?? [];

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-semibold text-charcoal-900">{t("patient.dashboard.title")}</h1>
        <p className="mt-1 text-charcoal-500">{t("patient.dashboard.welcome", { name: user?.email })}</p>
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        <StatCard label={t("patient.dashboard.medicationsCount")} value={activeMeds.length} icon={<Pill className="h-5 w-5" />} />
        <StatCard
          label={t("patient.dashboard.nextAppointment")}
          value={upcoming[0] ? new Date(upcoming[0].scheduledAt).toLocaleDateString() : t("patient.dashboard.noAppointment")}
          icon={<CalendarDays className="h-5 w-5" />}
        />
        <StatCard label={t("patient.dashboard.openProblems")} value={openAssessments.length} icon={<AlertTriangle className="h-5 w-5" />} />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t("patient.dashboard.quickActions")}</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-3">
          <Link to="/patient/appointments" className={cn(buttonVariants())}>
            <CalendarDays className="h-4 w-4" /> {t("patient.dashboard.bookAppointment")}
          </Link>
          <Link to="/patient/medications" className={cn(buttonVariants({ variant: "outline" }))}>
            <Pill className="h-4 w-4" /> {t("patient.dashboard.addMedication")}
          </Link>
          <Link to="/patient/mtm" className={cn(buttonVariants({ variant: "outline" }))}>
            <ClipboardList className="h-4 w-4" /> {t("patient.mtm.title")}
          </Link>
          <Link to="/patient/reports" className={cn(buttonVariants({ variant: "ghost" }))}>
            {t("patient.dashboard.viewReports")}
          </Link>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t("patient.appointments.upcoming")}</CardTitle>
        </CardHeader>
        <CardContent>
          {upcoming.length === 0 ? (
            <EmptyState title={t("patient.appointments.empty")} />
          ) : (
            <ul className="divide-y divide-charcoal-100">
              {upcoming.map((a) => (
                <li key={a.id} className="flex items-center justify-between py-3 text-sm">
                  <span className="text-charcoal-700">{new Date(a.scheduledAt).toLocaleString()}</span>
                  <span className="text-charcoal-400">{a.type.replaceAll("_", " ")}</span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
